import { z } from 'zod';

import { BooleanQueryParamSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * City Master shapes (Configuration → City Management | CMS).
 *
 * Layered like the other CRUD surfaces:
 *   - CityListItem  - light grid row (state name + city id + code + country)
 *   - CityDetail    - the edit drawer payload (adds lat/lon/timezone/audit)
 *   - Create/Update - the operator write paths
 *
 * Mapping to the legacy "Add Query Source" form:
 *   City Id          -> legacyId        (unique upstream source id)
 *   City Code        -> iataCode        (canonical 3-letter code)
 *   Custom City Code -> customCityCode  (operator override, 3 letters)
 *   State Name       -> resolved to a `states` row via find-or-create
 *   Country          -> countryId       (selected from the seeded master)
 *
 * Country/State are normalized FKs (see geo.ts), not the free-text strings the
 * legacy screen stored. The 3-letter codes are optional; when set they must be
 * exactly three uppercase letters.
 */

/** Exactly three uppercase letters, e.g. "BOM". Matches the legacy rule. */
export const CityCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Z]{3}$/, 'Must be exactly 3 uppercase letters');

/**
 * Optional city code for create/update. Blank / null → null; when set must
 * still be exactly 3 uppercase letters. Avoids `z.preprocess` so react-hook-form
 * resolvers keep a concrete string input type.
 */
export const OptionalCityCodeSchema = z
  .union([CityCodeSchema, z.literal(''), z.null()])
  .transform((val): string | null => (val === '' || val === null ? null : val));

// ---------------------------------------------------------------------------
// Reference sub-schemas
// ---------------------------------------------------------------------------

const CountryRefSchema = z.object({
  id: UuidSchema,
  isoCode: z.string(),
  name: z.string(),
});
const StateRefSchema = z.object({ id: UuidSchema, name: z.string() });

// ---------------------------------------------------------------------------
// List + detail
// ---------------------------------------------------------------------------

export const CityListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  name: z.string(),
  iataCode: z.string().nullable(),
  customCityCode: z.string().nullable(),
  /** Neighbouring airports (IATA) offered beside the city's own code for flights. */
  neighbouringAirportCodes: z.array(z.string()).default([]),
  /** Linked TBO destination CityId (soft reference to tbo_cities). */
  tboCityId: z.string().nullable(),
  /** Extra TBO CityIds searched alongside `tboCityId` so hotel search fans out
   *  across nearby TBO regions (e.g. Ubud + Payangan) and merges the results. */
  additionalTboCityIds: z.array(z.string()).default([]),
  state: StateRefSchema.nullable(),
  country: CountryRefSchema,
  isPopular: z.boolean(),
  isActive: z.boolean(),
  /**
   * Operator flag - when true this city is published to the website
   * (destination pickers, content authoring). See ADR-0031.
   */
  isWebsite: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type CityListItem = z.infer<typeof CityListItemSchema>;

export const CityListQuerySchema = PaginationQuerySchema.extend({
  // `search` (inherited from PaginationQuerySchema) is the case-insensitive
  // term, matched server-side across city name / state name / iata code /
  // custom code. The client only sends it once it is >= 3 chars.
  countryId: UuidSchema.optional(),
  /**
   * Inverse of `countryId` - returns cities NOT in the given country.
   * Used by the lead form's "International" departure-city filter so
   * the dropdown excludes domestic cities once the operator picks the
   * INTERNATIONAL lead category. When both `countryId` and
   * `excludeCountryId` are supplied, the result is intersected (i.e.
   * empty unless they happen to match - which is a caller bug).
   */
  excludeCountryId: UuidSchema.optional(),
  stateId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  isPopular: BooleanQueryParamSchema.optional(),
  isWebsite: BooleanQueryParamSchema.optional(),
  /**
   * Restrict the result set to cities whose parent country is itself
   * active (`countries.is_active = true`). The lead-form pickers send
   * `countryIsActive=true` together with `isActive=true` so deactivating
   * a country immediately hides every city under it from intake, even
   * if the bulk-activate scripts haven't been re-run. The admin City
   * Management screen leaves this off so admins still see every city.
   */
  countryIsActive: BooleanQueryParamSchema.optional(),
  /**
   * Filter by whether `tbo_city_id` is set. `true` = only cities linked
   * to a TBO destination; `false` = only unlinked cities. Used by the
   * admin City Management screen so ops can see how the post-seed
   * matcher landed + spot cities that still need a manual TBO id.
   * ADR-0019 / 2026-06-24.
   *
   * NB: uses `BooleanQueryParamSchema` (string-aware) rather than
   * `z.coerce.boolean()` because `z.coerce.boolean('false') === true`
   * (any non-empty string is truthy under the standard JS coerce).
   * The string-aware preprocessor maps 'false'/'0'/'no' to `false`.
   */
  hasTboCityId: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
  /**
   * 2026-06-27 / ADR-0058. Controls the `search` matcher mode for the city name:
   *   - `contains` (default): case-insensitive substring (`ILIKE
   *     '%term%'`). Matches "Mumbai" for both "mum" and "umb". The
   *     historical behavior; used by the admin City Management
   *     screen + every non-leads picker.
   *   - `prefix`: case-insensitive starts-with (`ILIKE 'term%'`).
   *     Used by city combobox/picker dropdowns so typing "del"
   *     returns Delhi / Dehradun only - matches the operator's
   *     typing rhythm and excludes state-name noise (e.g. every
   *     city under Delhi state). Matches city name, IATA code, and
   *     custom city code with prefix only; state name is excluded.
   *     See docs/adr/0058-city-picker-prefix-search.md.
   */
  searchMode: z.enum(['contains', 'prefix']).optional(),
});
export type CityListQuery = z.infer<typeof CityListQuerySchema>;

export const CityListResponseSchema = paginatedResponseSchema(CityListItemSchema);
export type CityListResponse = z.infer<typeof CityListResponseSchema>;

export const CityDetailSchema = CityListItemSchema.extend({
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  timezone: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CityDetail = z.infer<typeof CityDetailSchema>;

// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------

const CityCoreFieldsSchema = z.object({
  countryId: UuidSchema.describe('Country UUID. Required; use GET /countries.'),
  /** Selected state under the country. The admin picks from GET /states. */
  stateId: UuidSchema.nullable().optional(),
  /**
   * Alternative to `stateId`: a free-text name resolved (find-or-create) to a
   * `states` row under the country. Retained for non-UI callers (ETL/API); the
   * admin form sends `stateId` from the states dropdown.
   */
  stateName: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(1, 'City name is required').max(200),
  /** "City Code" - canonical 3-letter code. Optional; blank stores null. */
  iataCode: OptionalCityCodeSchema,
  /** "Custom City Code" - operator override, 3 letters. Optional; blank stores null. */
  customCityCode: OptionalCityCodeSchema,
  /**
   * Neighbouring airports (IATA, 3 uppercase letters each, max 10) a traveller
   * to this city may fly via. The whole list is replaced on update; omit to
   * leave it as is.
   */
  neighbouringAirportCodes: z.array(CityCodeSchema).max(10).optional(),
  /** "City Id" - upstream source id. Unique; omit (or 0) for none. */
  legacyId: z.number().int().positive().nullable().optional(),
  /** Linked TBO destination CityId, picked from the TBO-city dropdown. */
  tboCityId: z.string().trim().max(64).nullable().optional(),
  /** Extra TBO CityIds searched alongside `tboCityId` (nearby regions). */
  additionalTboCityIds: z.array(z.string().trim().max(64)).max(20).optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  timezone: z.string().trim().max(64).nullable().optional(),
  isPopular: z.boolean().optional(),
  isActive: z.boolean().optional(),
  /**
   * Operator opt-in to publish this city to the website (destinations,
   * content authoring). Default false. See ADR-0031.
   */
  isWebsite: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

/**
 * State is optional (2026-10-19): some countries carry no usable states in the
 * master (e.g. city-states, or states not yet activated), and a city must still
 * be addable. Pass `stateId` or `stateName` when there is one.
 */
export const CreateCityRequestSchema = CityCoreFieldsSchema;
export type CreateCityRequest = z.infer<typeof CreateCityRequestSchema>;

/** Partial: pass only the fields to change. `stateId`/`stateName` re-resolve the state. */
export const UpdateCityRequestSchema = CityCoreFieldsSchema.partial();
export type UpdateCityRequest = z.infer<typeof UpdateCityRequestSchema>;
